import { useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { ingredientsApi } from '@/api/ingredients';
import { suppliersApi }   from '@/api/suppliers';
import { useToast }       from '@/providers/ToastProvider';
import { getErrorMessage } from '@/api/client';
import {
  PageHeader, Button, Input, Select, FormField, Card, CardHeader, CardTitle, CardContent, Spinner,
} from '@/components/ui';
import { ArrowLeft } from 'lucide-react';

const schema = z.object({
  name:         z.string().min(1, 'Required'),
  category_id:  z.string().optional(),
  supplier_id:  z.string().optional(),
  unit_size:    z.coerce.number().positive('Must be > 0'),
  unit_price:   z.coerce.number().min(0),
  vat_rate:     z.coerce.number().min(0).max(1),
  sugars:       z.coerce.number().min(0),
  sml:          z.coerce.number().min(0),
  fat:          z.coerce.number().min(0),
  solids_diff:  z.coerce.number().min(0),
  solids_total: z.coerce.number().min(0),
  pod:          z.coerce.number().min(0),
  pac:          z.coerce.number().min(0),
  is_active:    z.boolean().optional(),
});

type FormValues = z.infer<typeof schema>;

export default function IngredientFormPage() {
  const { t }        = useTranslation();
  const { id }       = useParams<{ id?: string }>();
  const isEdit       = !!id;
  const navigate     = useNavigate();
  const { toast }    = useToast();
  const qc           = useQueryClient();

  const { data: ingredient, isLoading } = useQuery({
    queryKey: ['ingredient', id],
    queryFn:  () => ingredientsApi.get(id!),
    enabled:  isEdit,
  });

  const { data: categories } = useQuery({ queryKey: ['ingredient-categories'], queryFn: ingredientsApi.listCategories });
  const { data: suppliers  } = useQuery({ queryKey: ['suppliers-all'],         queryFn: suppliersApi.listAll });

  const {
    register, handleSubmit, reset,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({ resolver: zodResolver(schema) });

  useEffect(() => {
    if (ingredient) {
      reset({
        name:         ingredient.name,
        category_id:  ingredient.category_id ?? '',
        supplier_id:  ingredient.supplier_id ?? '',
        unit_size:    ingredient.unit_size,
        unit_price:   ingredient.unit_price,
        vat_rate:     ingredient.vat_rate,
        sugars:       ingredient.sugars,
        sml:          ingredient.sml,
        fat:          ingredient.fat,
        solids_diff:  ingredient.solids_diff,
        solids_total: ingredient.solids_total,
        pod:          ingredient.pod,
        pac:          ingredient.pac,
        is_active:    ingredient.is_active,
      });
    }
  }, [ingredient, reset]);

  const saveMutation = useMutation({
    mutationFn: (data: FormValues) => {
      const payload = {
        ...data,
        category_id: data.category_id || null,
        supplier_id: data.supplier_id || null,
      };
      return isEdit
        ? ingredientsApi.update(id!, payload)
        : ingredientsApi.create(payload);
    },
    onSuccess: () => {
      toast(t('ingredients.saved'));
      qc.invalidateQueries({ queryKey: ['ingredients'] });
      navigate('/ingredients');
    },
    onError: (err) => toast(getErrorMessage(err), 'error'),
  });

  if (isEdit && isLoading) return <Spinner className="py-20" />;

  return (
    <div>
      <PageHeader title={isEdit ? t('ingredients.edit') : t('ingredients.new')}>
        <Button asChild variant="outline" size="sm">
          <Link to="/ingredients"><ArrowLeft size={15} /> {t('common.back')}</Link>
        </Button>
      </PageHeader>

      <form onSubmit={handleSubmit(d => saveMutation.mutate(d))} className="space-y-6 max-w-3xl">
        {/* Basic info */}
        <Card>
          <CardHeader><CardTitle>{t('common.name')}</CardTitle></CardHeader>
          <CardContent className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <FormField label={t('ingredients.name')} error={errors.name?.message} required className="sm:col-span-2">
              <Input {...register('name')} />
            </FormField>
            <FormField label={t('ingredients.category')}>
              <Select {...register('category_id')}
                placeholder="— Select category —"
                options={categories?.map(c => ({ value: c.id, label: c.name })) ?? []}
              />
            </FormField>
            <FormField label={t('ingredients.supplier')}>
              <Select {...register('supplier_id')}
                placeholder="— Select supplier —"
                options={suppliers?.map(s => ({ value: s.id, label: s.name })) ?? []}
              />
            </FormField>
          </CardContent>
        </Card>

        {/* Pricing */}
        <Card>
          <CardHeader><CardTitle>{t('ingredients.pricing')}</CardTitle></CardHeader>
          <CardContent className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <FormField label={t('ingredients.unitSize')} error={errors.unit_size?.message} required>
              <Input type="number" step="0.001" {...register('unit_size')} />
            </FormField>
            <FormField label={t('ingredients.unitPrice')} error={errors.unit_price?.message} required>
              <Input type="number" step="0.0001" {...register('unit_price')} />
            </FormField>
            <FormField label={t('ingredients.vatRate')} error={errors.vat_rate?.message} required>
              <Input type="number" step="0.01" placeholder="e.g. 0.10" {...register('vat_rate')} />
            </FormField>
          </CardContent>
        </Card>

        {/* Technical values */}
        <Card>
          <CardHeader>
            <CardTitle>{t('ingredients.technicalValues')}</CardTitle>
            <p className="text-xs text-muted-foreground">All values are expressed as g per 100g of ingredient (% by weight). POD and PAC are index values relative to sucrose = 100.</p>
          </CardHeader>
          <CardContent className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            {([ 'sugars', 'sml', 'fat', 'solids_diff', 'solids_total', 'pod', 'pac' ] as const).map(field => (
              <FormField key={field} label={t(`ingredients.${field === 'solids_diff' ? 'solidsDiff' : field === 'solids_total' ? 'solidsTotal' : field}`)} error={(errors as Record<string, {message?: string}>)[field]?.message}>
                <Input type="number" step="0.01" min="0" {...register(field)} />
              </FormField>
            ))}
          </CardContent>
        </Card>

        <div className="flex gap-3">
          <Button type="submit" loading={isSubmitting || saveMutation.isPending}>
            {t('common.save')}
          </Button>
          <Button type="button" variant="outline" onClick={() => navigate('/ingredients')}>
            {t('common.cancel')}
          </Button>
        </div>
      </form>
    </div>
  );
}
